export type EntityType = 'campaign' | 'adset' | 'ad';
export interface EntityRef { type: EntityType; id: string; adAccountId: string; }
export type ReportKind = 'ads' | 'campaigns' | 'creative' | 'geo' | 'accounts';
export type ReportIdentity =
  | { rowKind: 'ads' | 'campaigns'; rowKey: string; entityRef: EntityRef | null }
  | { rowKind: 'creative' | 'geo' | 'accounts'; rowKey: string; entityRef: null };
export function entityKey(type: EntityType, adAccountId: string, id: string): string {
  return JSON.stringify([type, adAccountId, id]);
}
export function campaignAggregationKey(row: { ad_account_id: string; campaign_id?: string | null; ad_id?: string; campaign_fallback_ad_id?: string | null }): string {
  return entityKey('campaign', row.ad_account_id, row.campaign_id?.trim() || 'missing-ad:' + (row.campaign_fallback_ad_id || row.ad_id || 'unknown'));
}
export function creativeToken(name: string | null | undefined): string | null {
  if (!name) return null;
  const parts = name.split('_');
  if (parts.length < 2) return null;
  const token = parts[parts.length - 2].trim().normalize('NFC');
  return token && token !== 'UNKNOWN' ? token : null;
}
export function creativeId(token: string): string { return 'business-creative:' + encodeURIComponent(token.normalize('NFC')); }
/** Badges require the complete naming convention, not a partial prefix. */
export function campaignBadge(name: string | null | undefined): string | null {
  if (!name) return null;
  const match = name.trim().normalize('NFC').match(/^([A-Z]{2}|Lang_[A-Z]{2}|[A-Z]{2}_Lang)_[0-9]+_CBO_[^_\s]+_([0-9]{2})-([0-9]{2})-([0-9]{4})$/u);
  if (!match) return null;
  const [, badge, day, month, year] = match;
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0,10) === `${year}-${month}-${day}` ? badge : null;
}
export function isCampaignBadge(value: string): boolean {
  return campaignBadge(value + '_0_CBO_creative_01-01-2000') === value;
}
export function creativeTokenFromId(id: string): string|null {
  if(!id.startsWith('business-creative:'))return null;
  try {
    const token=decodeURIComponent(id.slice('business-creative:'.length));
    return token && creativeId(token)===id ? token : null;
  }catch{return null;}
}
export function reportIdentity<K extends ReportKind>(kind: K, row: Record<string, any>): { rowKind: K; rowKey: string; entityRef: K extends 'ads'|'campaigns' ? EntityRef|null : null };
export function reportIdentity(kind: ReportKind, row: Record<string, any>): ReportIdentity {
  if (kind === 'ads' || kind === 'campaigns') {
    const type: EntityType = kind === 'ads' ? 'ad' : 'campaign';
    const id = type === 'ad' ? row.ad_id : row.campaign_id;
    const account = String(row.ad_account_id || '');
    const entityRef = id && account ? { type, id: String(id), adAccountId: account } : null;
    return { rowKind: kind, rowKey: type === 'ad' ? entityKey(type, account, String(id || 'missing')) : campaignAggregationKey(row as any), entityRef };
  }
  const id = kind === 'creative' ? row.creative_id || creativeId(String(row.creative || 'UNKNOWN')) : kind === 'geo' ? row.country : row.ad_account_id;
  return { rowKind: kind, rowKey: JSON.stringify([kind, String(id || 'unknown')]), entityRef: null };
}
